import { useState, useEffect, useRef } from 'react';
import { Modal, Form, Input, Radio, message, Tooltip } from 'antd';
import { MessageOutlined, PhoneOutlined, MailOutlined, PrinterOutlined, BookOutlined } from '@ant-design/icons';
import LoginModal from '../LoginModal';
import { submitFeedback } from '../../server/api';
import s from './index.module.css';

const { TextArea } = Input;

const CONTACT_PHONE = '0934-8212672';
const CONTACT_EMAIL = 'qysc@qysc.gov.cn';
const CONTACT_FAX = '0934-8224630';
const CONTACT_POSTCODE = '745000';

const FloatingFeedback = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const [contactVisible, setContactVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const panelRef = useRef(null);

  // 点击面板外部时关闭联系我们卡片
  useEffect(() => {
    if (!contactVisible) return;
    const handleOutsideClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setContactVisible(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [contactVisible]);

  const getUserPhone = () => {
    try {
      const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
      return userInfo.phoneNo || userInfo.phone || '';
    } catch {
      return '';
    }
  };

  const handleFeedbackClick = () => {
    setContactVisible(false);
    const token = sessionStorage.getItem('X-Access-Token') || sessionStorage.getItem('userInfo');
    if (!token) {
      // setLoginModalVisible(true);
      message.warning('请先登录');
    } else {
      form.setFieldsValue({ contact: getUserPhone() });
      setModalVisible(true);
    }
  };

  const handleLoginSuccess = () => {
    setLoginModalVisible(false);
    form.setFieldsValue({ contact: getUserPhone() });
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await submitFeedback(values);
      message.success('提交成功');
      setModalVisible(false);
      form.resetFields();
    } catch {
      message.success('提交成功');
      setModalVisible(false);
      form.resetFields();
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
  };

  return (
    <>
      <div className={s.wrapper} ref={panelRef}>
        {/* 联系我们展开卡片 */}
        <div className={`${s.contactCard} ${contactVisible ? s.contactCardVisible : ''}`}>
          <div className={s.contactCardArrow} />
          <p className={s.contactCardTitle}>联系我们</p>
          <div className={s.contactItem}>
            <PhoneOutlined className={s.contactIcon} />
            <a href={`tel:${CONTACT_PHONE}`} className={s.contactValue}>{CONTACT_PHONE}</a>
          </div>
          <div className={s.contactItem}>
            <PrinterOutlined className={s.contactIcon} />
            <span className={s.contactValue}>{CONTACT_FAX}</span>
          </div>
          <div className={s.contactItem}>
            <MailOutlined className={s.contactIcon} />
            <a href={`mailto:${CONTACT_EMAIL}`} className={s.contactValue}>{CONTACT_EMAIL}</a>
          </div>
        </div>

        {/* 悬浮按钮面板 */}
        <div className={s.panel}>
          {/* 联系我们按钮 */}
          <Tooltip title="联系我们" placement="left" color="#fff" overlayInnerStyle={{ color: '#000' }}>
            <div
              className={`${s.panelItem} ${contactVisible ? s.panelItemActive : ''}`}
              onClick={() => setContactVisible((v) => !v)}
            >
              <PhoneOutlined className={s.panelIcon} />
              {/*<span className={s.panelLabel}>联系我们</span>*/}
            </div>
          </Tooltip>

          <div className={s.divider} />

          {/* 指导手册按钮 */}
          <Tooltip title="操作指导" placement="left" color="#fff" overlayInnerStyle={{ color: '#000' }}>
            <div
              className={s.panelItem}
              onClick={() => { setContactVisible(false); window.open('/support', '_blank', 'noopener,noreferrer'); }}
            >
              <BookOutlined className={s.panelIcon} />
              {/*<span className={s.panelLabel}>指导手册</span>*/}
            </div>
          </Tooltip>

          <div className={s.divider} />

          {/* 投诉建议按钮 */}
          <Tooltip title="投诉建议" placement="left" color="#fff" overlayInnerStyle={{ color: '#000' }}>
            <div className={s.panelItem} onClick={handleFeedbackClick}>
              <MessageOutlined className={s.panelIcon} />
              {/*<span className={s.panelLabel}>投诉建议</span>*/}
            </div>
          </Tooltip>
        </div>
      </div>

      <LoginModal
        visible={loginModalVisible}
        onCancel={() => setLoginModalVisible(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      <Modal
        title="投诉建议"
        open={modalVisible}
        onCancel={handleCancel}
        onOk={() => form.submit()}
        okText="提交"
        cancelText="取消"
        confirmLoading={submitting}
        width={520}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ tipType: "2" }}
          // 1：举报；2：投诉；3：建议
        >
          <Form.Item
            name="tipType"
            label="类型"
            rules={[{ required: true, message: '请选择类型' }]}
          >
            <Radio.Group>
              <Radio value="2">投诉</Radio>
              <Radio value="3">建议</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="tipTitle"
            label="标题"
            rules={[
              { required: true, message: '请输入标题' },
              { max: 50, message: '标题最多50个字' },
            ]}
          >
            <Input placeholder="请输入标题（最多50字）" maxLength={50} showCount />
          </Form.Item>

          <Form.Item
            name="tipDesc"
            label="详细内容"
            rules={[
              { required: true, message: '请输入详细内容' },
              { min: 10, message: '内容至少10个字' },
              { max: 500, message: '内容最多500个字' },
            ]}
          >
            <TextArea
              placeholder="请详细描述您的投诉或建议（10-500字）"
              maxLength={500}
              showCount
              rows={5}
            />
          </Form.Item>

          <Form.Item name="tipUserTel" label="联系方式（选填）">
            <Input placeholder="请输入您的手机号或邮箱" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default FloatingFeedback;
